import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import API from "../../config/Api";
import { AuthContext } from "../../context/AuthContext";
import { trapDialogTab } from "../../utils/trapDialogTab";
import "./departmentMessages.css";

const RequiredDepartmentMessageInbox = () => {
    const { user, token } = useContext(AuthContext);
    const [pending, setPending] = useState([]);
    const [response, setResponse] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const textareaRef = useRef(null);
    const dialogRef = useRef(null);
    const returnFocusRef = useRef(null);
    const identity = user?.Id ?? user?.id ?? user?.email;

    const refresh = useCallback(async () => {
        if (!identity || !token) return;
        try {
            const result = await API.get("/department-messages/pending");
            setPending(Array.isArray(result.data) ? result.data : []);
        } catch (requestError) {
            // The application remains usable if the inbox service is temporarily unavailable.
        }
    }, [identity, token]);

    useEffect(() => {
        if (!identity || !token) {
            setPending([]);
            return undefined;
        }
        refresh();
        const timer = window.setInterval(refresh, 20000);
        return () => window.clearInterval(timer);
    }, [identity, refresh, token]);

    const active = pending[0];
    const activeId = active?.recipientId;

    useEffect(() => {
        if (activeId == null) {
            if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
            returnFocusRef.current = null;
            return undefined;
        }
        if (!returnFocusRef.current) returnFocusRef.current = document.activeElement;
        setResponse("");
        setError("");
        const onKeyDown = (event) => trapDialogTab(event, dialogRef.current);
        window.addEventListener("keydown", onKeyDown);
        const focusTimer = window.setTimeout(() => textareaRef.current?.focus(), 80);
        return () => {
            window.clearTimeout(focusTimer);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [activeId]);

    const submit = async (event) => {
        event.preventDefault();
        if (!active || !response.trim() || saving) return;
        setSaving(true);
        setError("");
        try {
            await API.post(`/department-messages/${active.recipientId}/response`, { response: response.trim() });
            setPending((current) => current.filter((message) => message.recipientId !== active.recipientId));
            setResponse("");
        } catch (requestError) {
            setError(requestError?.response?.data?.message || requestError?.message || "Votre reponse n'a pas pu etre enregistree.");
        } finally {
            setSaving(false);
        }
    };

    if (!active) return null;

    return createPortal(
        <div className="department-message-required-backdrop">
            <section ref={dialogRef} className="department-message-required-dialog" role="dialog" aria-modal="true" aria-labelledby="department-message-required-title" aria-describedby="department-message-required-description" tabIndex="-1">
                <div className="department-message-required-topline" />
                <header className="department-message-required-header">
                    <span className="department-message-required-icon" aria-hidden="true"><i className="bx bx-message-square-dots" /></span>
                    <div><span>Reponse requise{pending.length > 1 ? ` · ${pending.length} messages` : ""}</span><h2 id="department-message-required-title">{active.title}</h2></div>
                </header>
                <div className="department-message-required-body">
                    <div className="department-message-required-byline">
                        <span>De <strong>{active.senderEmail}</strong></span>
                        <span>{active.departments}</span>
                    </div>
                    <p className="department-message-required-copy" id="department-message-required-description">{active.message}</p>
                    <form onSubmit={submit}>
                        <label className="department-message-field">
                            <span id="department-message-reply-label">Votre reponse</span>
                            <textarea ref={textareaRef} aria-labelledby="department-message-reply-label" value={response} maxLength={3000} rows={5} onChange={(event) => setResponse(event.target.value)} placeholder="Ecrivez votre reponse..." required />
                            <small>{response.length}/3000</small>
                        </label>
                        {error && <p className="department-message-error" role="alert"><i className="bx bx-error-circle" aria-hidden="true" />{error}</p>}
                        <div className="department-message-required-footer">
                            <span>Vous devez envoyer une reponse pour continuer.</span>
                            <button type="submit" className="department-message-primary" disabled={saving || !response.trim()}>
                                <i className={`bx ${saving ? "bx-loader-alt bx-spin" : "bx-send"}`} aria-hidden="true" />
                                {saving ? "Enregistrement..." : "Envoyer ma reponse"}
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </div>, document.body);
};

export default RequiredDepartmentMessageInbox;
